/**
 * Pure canvas-trim math for the Icon Drop Play Again lockup.
 * Display-time only — does not rewrite campaign uploads.
 */

export interface ContentBbox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface LockupInsets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface CssTrimFractions {
  bboxWidth: number;
  bboxHeight: number;
  left: string;
  top: string;
  width: string;
  height: string;
}

export const LOCKUP_PAD_MIN = -40;
export const LOCKUP_PAD_MAX = 50;
export const DEFAULT_ALPHA_CUT = 8;
export const DEFAULT_UNIFORM_DELTA = 8;
/** Skip auto-crop when unused canvas is under this share on both axes. */
export const TINY_UNUSED_FRACTION = 0.08;

/** Last-resort Payday game crop when CORS blocks canvas read (1280×720). */
export const PAYDAY_GAME_FALLBACK_BBOX: ContentBbox = { x: 0, y: 259, width: 1280, height: 184 };
/** Last-resort Payday studio crop when CORS blocks canvas read (724×314). */
export const PAYDAY_STUDIO_FALLBACK_BBOX: ContentBbox = { x: 2, y: 15, width: 722, height: 265 };
/** Last-resort Dragonwilds game crop when CORS blocks canvas read (1280×720). */
export const DRAGONWILDS_GAME_FALLBACK_BBOX: ContentBbox = { x: 38, y: 215, width: 1215, height: 292 };

const isInkPixel = (
  r: number,
  g: number,
  b: number,
  a: number,
  corner: readonly [number, number, number, number],
  alphaCut: number,
  uniformDelta: number,
): boolean => {
  if (a <= alphaCut) return false;
  const nearCorner =
    Math.abs(r - corner[0]) <= uniformDelta &&
    Math.abs(g - corner[1]) <= uniformDelta &&
    Math.abs(b - corner[2]) <= uniformDelta &&
    Math.abs(a - corner[3]) <= uniformDelta;
  return !(nearCorner && corner[3] <= alphaCut);
};

export const findContentBbox = (
  rgba: ArrayLike<number>,
  width: number,
  height: number,
  options?: { alphaCut?: number; uniformDelta?: number },
): ContentBbox | null => {
  const alphaCut = options?.alphaCut ?? DEFAULT_ALPHA_CUT;
  const uniformDelta = options?.uniformDelta ?? DEFAULT_UNIFORM_DELTA;
  if (width <= 0 || height <= 0 || rgba.length < width * height * 4) return null;

  const sample = (index: number): [number, number, number, number] => [
    rgba[index],
    rgba[index + 1],
    rgba[index + 2],
    rgba[index + 3],
  ];
  const c0 = sample(0);
  const c1 = sample((width - 1) * 4);
  const c2 = sample((height - 1) * width * 4);
  const c3 = sample(((height - 1) * width + (width - 1)) * 4);
  const corner: [number, number, number, number] = [
    (c0[0] + c1[0] + c2[0] + c3[0]) / 4,
    (c0[1] + c1[1] + c2[1] + c3[1]) / 4,
    (c0[2] + c1[2] + c2[2] + c3[2]) / 4,
    (c0[3] + c1[3] + c2[3] + c3[3]) / 4,
  ];

  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const i = (y * width + x) * 4;
      if (!isInkPixel(rgba[i], rgba[i + 1], rgba[i + 2], rgba[i + 3], corner, alphaCut, uniformDelta)) {
        continue;
      }
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }
  if (maxX < 0) return null;
  return { x: minX, y: minY, width: maxX - minX + 1, height: maxY - minY + 1 };
};

export const isBboxWorthTrimming = (bbox: ContentBbox, width: number, height: number): boolean => {
  if (width <= 0 || height <= 0) return false;
  const unusedX = 1 - bbox.width / width;
  const unusedY = 1 - bbox.height / height;
  return unusedX > TINY_UNUSED_FRACTION || unusedY > TINY_UNUSED_FRACTION;
};

export const applyAdminInsets = (
  bbox: ContentBbox,
  naturalWidth: number,
  naturalHeight: number,
  insets: LockupInsets,
): ContentBbox => {
  const extraLeft = (insets.left / 100) * naturalWidth;
  const extraRight = (insets.right / 100) * naturalWidth;
  const extraTop = (insets.top / 100) * naturalHeight;
  const extraBottom = (insets.bottom / 100) * naturalHeight;

  const x = Math.max(0, Math.min(bbox.x + extraLeft, naturalWidth - 1));
  const y = Math.max(0, Math.min(bbox.y + extraTop, naturalHeight - 1));
  const right = Math.max(x + 1, Math.min(bbox.x + bbox.width - extraRight, naturalWidth));
  const bottom = Math.max(y + 1, Math.min(bbox.y + bbox.height - extraBottom, naturalHeight));
  return { x, y, width: right - x, height: bottom - y };
};

export const resolveCssTrim = (bbox: ContentBbox, naturalWidth: number, naturalHeight: number): CssTrimFractions => ({
  bboxWidth: bbox.width,
  bboxHeight: bbox.height,
  left: `calc(-100% * ${bbox.x} / ${bbox.width})`,
  top: `calc(-100% * ${bbox.y} / ${bbox.height})`,
  width: `calc(100% * ${naturalWidth} / ${bbox.width})`,
  height: `calc(100% * ${naturalHeight} / ${bbox.height})`,
});

export const resolveKnownLogoTrim = (src: string, naturalWidth: number, naturalHeight: number): ContentBbox | null => {
  if (naturalWidth === 1280 && naturalHeight === 720 && /\/campaigns\/payday2\/gameLogo/i.test(src)) {
    return { ...PAYDAY_GAME_FALLBACK_BBOX };
  }
  if (naturalWidth === 1280 && naturalHeight === 720 && /\/campaigns\/runescape-dragonwilds\/gameLogo/i.test(src)) {
    return { ...DRAGONWILDS_GAME_FALLBACK_BBOX };
  }
  if (naturalWidth === 724 && naturalHeight === 314 && /\/campaigns\/payday2\/studioLogo/i.test(src)) {
    return { ...PAYDAY_STUDIO_FALLBACK_BBOX };
  }
  return null;
};

const cssLen = (value: string | number | undefined, fallback: string): string => {
  if (value == null) return fallback;
  return typeof value === 'number' ? `${value}px` : value;
};

/** Contain the trimmed ink inside the lockup slot (height + max-width). */
export const resolveTrimmedSlotStyle = (
  crop: CssTrimFractions,
  slot: { maxWidth?: string | number; maxHeight?: string | number; height?: string | number },
): { width: string; height: string } => {
  const maxWidth = cssLen(slot.maxWidth, '100%');
  const maxHeight = cssLen(slot.maxHeight ?? slot.height, '100%');
  return {
    width: `min(${maxWidth}, calc((${maxHeight}) * ${crop.bboxWidth} / ${crop.bboxHeight}))`,
    height: `min(${maxHeight}, calc((${maxWidth}) * ${crop.bboxHeight} / ${crop.bboxWidth}))`,
  };
};
